// M6.6 — FlashcardProgressWidget
// 2026-04-22: Refactor — widget CHỈ tập trung ÔN tập (thẻ đến hạn).
// 2026-04-24 (M39a): Gỡ "Tiến độ master theo bộ" (đã có ở FlashcardPage sidebar).
// 2026-04-24 (M39b): Redesign — bỏ badge góc phải + bỏ "(N đến hạn)" trong button,
//   số thẻ đến hạn chỉ hiển thị ở 1 chỗ DUY NHẤT (ô hero). Tránh 3 chỗ duplicate cũ.
//
// Layout:
//   [Header]          Ôn tập hôm nay · Flashcard SM-2
//   [Hero card]       Số thẻ đến hạn (số duy nhất) + subtitle
//   [Chart]           10 ngày tới (nếu có due trong tương lai)
//   [Footer]          Tra cứu | Nút CTA "Ôn ngay →"
//
// Props:
//   - data?     : kết quả GET /flashcard/progress (parent có thể fetch dùm)
//   - loading?  : boolean
//   - error?    : Error|null
//   Nếu parent không truyền → widget tự fetch (backward compat).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function FlashcardProgressWidget({ data: dataProp, loading: loadingProp, error: errorProp } = {}) {
  const [selfData, setSelfData] = useState(null);
  const [selfLoading, setSelfLoading] = useState(dataProp === undefined);
  const [selfErr, setSelfErr] = useState(null);

  // Tự fetch nếu parent không truyền data
  useEffect(() => {
    if (dataProp !== undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.flashcardProgress();
        if (!cancelled) setSelfData(r);
      } catch (e) {
        if (!cancelled) setSelfErr(e);
      } finally {
        if (!cancelled) setSelfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dataProp]);

  const data    = dataProp    !== undefined ? dataProp    : selfData;
  const loading = loadingProp !== undefined ? loadingProp : selfLoading;
  const err     = errorProp   !== undefined ? errorProp   : selfErr;

  if (loading) {
    return <Shell><div className="text-sm text-slate-400">Đang tải tiến trình…</div></Shell>;
  }
  if (err) {
    return <Shell><div className="text-sm text-rose-600">Không lấy được tiến trình flashcard</div></Shell>;
  }
  if (!data) {
    return <Shell><div className="text-sm text-slate-400">Chưa có dữ liệu</div></Shell>;
  }

  const { due_now = 0 } = data;
  const hasDue = due_now > 0;
  const upcomingTotal = (data.upcoming_10d_daily || []).reduce((s, d) => s + d.count, 0);
  // 2026-04-24 (M40), M85d (2026-04-26): Tổng thẻ đã học 10 ngày qua (cũ là 7).
  // Backward-compat: BE có thể trả past_10d_daily (mới) hoặc past_7d_daily (cũ).
  const past10dRaw = data.past_10d_daily || data.past_7d_daily || [];
  const past10Total = past10dRaw.reduce((s, d) => s + (d.total || 0), 0);

  return (
    <Shell>
      {/* M85e (2026-04-26): Hero + Heatmap song song trên md+ cho gọn.
          Mobile: stack 1 cột. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Hero (đã có CTA "Ôn ngay" inline) */}
        <HeroDueCount count={due_now} hasDue={hasDue} />

        {/* Calendar heatmap lịch sử 91 ngày — M41 + M85c future */}
        {data?.history_heatmap && data.history_stats && (
          <HistoryHeatmap
            days={data.history_heatmap}
            stats={data.history_stats}
          />
        )}
      </div>

      {/* 2 chart nhỏ cùng 1 row grid — M42 compact layout
          M85b (2026-04-26): Luôn hiện kể cả khi tổng = 0 — để NV biết chart
          tồn tại + tạo cảm giác "có khung sườn" thay vì trang trống. Chart
          tự xử lý empty state (hiển thị bars 0). */}
      {(past10dRaw.length > 0 || data?.upcoming_10d_daily?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-3 border-t border-slate-100">
          {past10dRaw.length > 0 && (
            <div>
              <Past10dChart daily={past10dRaw} compact />
            </div>
          )}
          {data?.upcoming_10d_daily?.length > 0 && (
            <div>
              <UpcomingChart daily={data.upcoming_10d_daily} compact />
            </div>
          )}
        </div>
      )}

      {/* Footer nhỏ — chỉ còn link Tra cứu (CTA đã move lên Hero) */}
      <div className="mt-3 pt-3 border-t border-slate-100 text-center">
        <Link
          to="/flashcards/browse"
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          🔎 Tra cứu câu hỏi theo bộ / chủ đề
        </Link>
      </div>
    </Shell>
  );
}

// ============================================================
// HeroDueCount — ô lớn số thẻ đến hạn + nút CTA "Ôn ngay" inline (M42)
// M154 (2026-05-13): thêm SpacedRepetitionInfo collapsible bên dưới
// ============================================================
function HeroDueCount({ count, hasDue }) {
  if (!hasDue) {
    return (
      <div className="rounded-2xl px-5 py-6 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
        <div className="text-center">
          <div className="text-5xl mb-2">🎉</div>
          <div className="text-base font-bold text-emerald-700">Đã ôn hết thẻ đến hạn</div>
          <div className="text-xs text-emerald-600/80 mt-1">
            Tuyệt vời — bạn đang nhớ tốt! Thẻ sẽ tự hiện lại khi đến lịch.
          </div>
        </div>
        <SpacedRepetitionInfo />
      </div>
    );
  }
  return (
    <div className="rounded-2xl px-5 py-5 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 border border-amber-200 shadow-sm">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Số lớn dominant */}
        <div className="shrink-0">
          <div className="flex items-baseline gap-1">
            <span className="text-5xl font-extrabold text-amber-600 tabular-nums leading-none">
              {count}
            </span>
            <span className="text-lg font-semibold text-amber-700/80">thẻ</span>
          </div>
          <div className="text-xs text-amber-700/90 mt-1 font-medium">đến hạn ôn</div>
        </div>
        {/* Message + CTA inline */}
        <div className="flex-1 min-w-0 text-xs text-amber-800/80 leading-relaxed">
          💡 Ôn xong thẻ đến hạn{' '}
          <b className="text-amber-900">trước</b> khi học bài mới để nhớ lâu hơn.
        </div>
        <Link
          to="/flashcards?mode=review"
          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg transition-all"
          data-testid="btn-review-now"
        >
          <span className="text-base">🔁</span>
          <span>Ôn ngay</span>
          <span className="text-base">→</span>
        </Link>
      </div>
      <SpacedRepetitionInfo />
    </div>
  );
}

// ============================================================
// M154 (2026-05-13) — SpacedRepetitionInfo
// 1 nút trigger → mở modal popup full nội dung. Popup CHỈ đóng được khi bấm
// × góc trên phải (KHÔNG đóng khi click backdrop, KHÔNG đóng khi ESC).
// ============================================================
function SpacedRepetitionInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="mt-4 pt-3 border-t border-amber-200/60">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-amber-900 hover:text-amber-700 px-4 py-2.5 rounded-xl bg-white/70 hover:bg-white border border-amber-200 shadow-sm transition"
        >
          <span className="text-base">🧠</span>
          <span>Vì sao ôn theo lịch giúp nhớ <em className="not-italic underline decoration-amber-400">cả đời</em>?</span>
        </button>
      </div>

      {open && <SpacedRepetitionDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function SpacedRepetitionDialog({ onClose }) {
  return (
    // Backdrop: KHÔNG đóng khi click ngoài (theo yêu cầu user — chỉ × mới đóng)
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sr-dialog-title"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header sticky với title + nút × */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 px-6 py-4 flex items-center justify-between gap-3 rounded-t-2xl">
          <h2 id="sr-dialog-title" className="text-base md:text-lg font-bold text-amber-900 flex items-center gap-2 min-w-0">
            <span className="text-xl">🧠</span>
            <span className="truncate">Vì sao ôn theo lịch giúp nhớ <em className="not-italic underline decoration-amber-400">cả đời</em>?</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition"
            title="Đóng"
            aria-label="Đóng"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 text-sm text-slate-700 leading-relaxed space-y-3">
          <p className="font-semibold text-slate-800">
            Não bạn quên rất nhanh — nhưng có cách "lừa" nó nhớ <strong>mãi mãi</strong>.
          </p>
          <p>
            Nhà tâm lý học <strong>Hermann Ebbinghaus</strong> phát hiện: sau khi học 1 lần, chỉ
            <b> 24h sau bạn đã quên 50%</b>, sau 1 tuần quên <b>90%</b>. Học gạo 10 lần liên tiếp
            trong 1 tối là cách <em>kém hiệu quả nhất</em>.
          </p>
          <p>
            Cách hiệu quả nhất gọi là <strong>Lặp lại Ngắt quãng</strong> (Spaced Repetition) —
            ôn lại kiến thức <strong>đúng vào lúc bạn sắp quên nó</strong>:
          </p>
          <ul className="space-y-1 pl-2">
            <li>📍 Lần 1: học → ôn lại sau <b>10 phút</b></li>
            <li>📍 Lần 2: ôn lại sau <b>1 ngày</b></li>
            <li>📍 Lần 3: ôn lại sau <b>3 ngày</b></li>
            <li>📍 Lần 4: ôn lại sau <b>1 tuần</b></li>
            <li>📍 Lần 5: ôn lại sau <b>2 tuần</b></li>
            <li>📍 ... khoảng cách dài dần: 1 tháng → 3 tháng → 6 tháng → cả đời</li>
          </ul>
          <p>
            <strong>App tự tính giúp bạn lịch này</strong> dựa vào việc bạn bấm
            <span className="mx-1 px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 font-semibold text-rose-700">❌ Quên</span>
            <span className="mx-1 px-1.5 py-0.5 rounded bg-orange-50 border border-orange-200 font-semibold text-orange-700">😬 Khó</span>
            <span className="mx-1 px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 font-semibold text-sky-700">😄 Dễ</span>
            <span className="mx-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 font-semibold text-emerald-700">👑 Siêu Dễ</span>.
            Bạn không cần nhớ ngày nào ôn câu nào — cứ vào app, app sẽ đưa đúng câu cần ôn.
          </p>

          <div className="pt-2 border-t border-amber-100">
            <div className="font-bold text-amber-800 mb-2">✨ Vì sao phương pháp này tuyệt vời</div>
            <ul className="space-y-2">
              <li>
                🎯 <strong>Học 15 phút/ngày = thuộc 100 câu trong 3 tuần</strong> — và nhớ
                <b> 6 tháng+</b> sau đó. Trong khi học gạo 5 tiếng liên tục chỉ nhớ được 3-4 ngày.
              </li>
              <li>
                💰 <strong>Tiết kiệm thời gian thật sự</strong> — không phải học lại từ đầu mỗi
                tuần. Mỗi câu chỉ ôn vài giây, vài lần là thuộc cả đời.
              </li>
              <li>
                🧠 <strong>Não nhớ kiểu "thật"</strong> — không phải nhớ tạm để qua bài kiểm tra
                rồi quên. Khi khách hỏi giữa cửa hàng, bạn <b>bật ra câu trả lời ngay</b> — không
                phải tra app.
              </li>
              <li>
                🚀 <strong>Phương pháp này được dùng bởi:</strong> bác sĩ học thuốc, người học
                ngoại ngữ (Duolingo), sinh viên y khoa Mỹ (Anki), phi công, kỹ sư hàng không.
              </li>
            </ul>
          </div>

          <div className="pt-3 mt-2 border-t border-amber-100 text-center">
            <p className="text-amber-900 font-semibold">
              👉 <strong>Tin não bạn.</strong> Bấm Quên khi quên thật.
              App sẽ lo phần còn lại — và bạn sẽ ngạc nhiên với chính mình sau 3 tuần.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Past10dChart — 10 ngày qua (số card đã học/ôn) — M85d (2026-04-26)
// Trước đây: Past7dChart 7 ngày — đổi sang 10 ngày + label rõ "Số card đã học".
// Bar stacked: new_cards (indigo) + reviews (emerald) cho mỗi ngày.
// ============================================================
function Past10dChart({ daily, compact = false }) {
  if (!Array.isArray(daily) || daily.length === 0) return null;
  const max = Math.max(1, ...daily.map(d => d.total || 0));
  const total = daily.reduce((s, d) => s + (d.total || 0), 0);
  const avg = Math.round(total / daily.length);
  // Label rút gọn theo thứ hiện local
  const dayName = (isoDate, isToday) => {
    if (isToday) return 'Nay';
    const d = new Date(isoDate + 'T00:00:00');
    const wd = ['CN','T2','T3','T4','T5','T6','T7'][d.getDay()];
    return wd;
  };
  const todayIso = daily[daily.length - 1]?.date;

  return (
    <div className={compact ? '' : 'mt-4 pt-3 border-t border-slate-100'}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
          📆 Số thẻ đã học 10 ngày qua
        </div>
        <div className="text-[11px] text-slate-500">
          Đã học <b className="text-slate-700">{total}</b> thẻ
          {total > 0 && <> · TB <b className="text-slate-700">{avg}</b>/ngày</>}
        </div>
      </div>
      <div className="flex gap-1">
        {daily.map((d, i) => {
          const newCards = d.new_cards || 0;
          const reviews = d.reviews || 0;
          const dayTotal = d.total || 0;
          const heightPct = max > 0 ? (dayTotal / max) * 100 : 0;
          const newPortion = dayTotal > 0 ? (newCards / dayTotal) * heightPct : 0;
          const reviewPortion = dayTotal > 0 ? (reviews / dayTotal) * heightPct : 0;
          const isToday = d.date === todayIso;

          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center group"
              title={`${d.date}\n📘 Mới: ${newCards}\n🔁 Ôn: ${reviews}\nTổng: ${dayTotal}`}
            >
              <div className="text-[10px] text-slate-600 font-semibold h-4 leading-none">
                {dayTotal > 0 ? dayTotal : ''}
              </div>
              {/* Stacked bar: emerald (ôn) dưới, indigo (mới) trên */}
              <div className="h-36 w-full flex flex-col-reverse">
                <div
                  className="w-full bg-emerald-400 group-hover:bg-emerald-500 transition-all"
                  style={{ height: `${reviewPortion}%` }}
                />
                <div
                  className="w-full bg-indigo-400 group-hover:bg-indigo-500 transition-all rounded-t"
                  style={{ height: `${newPortion}%` }}
                />
              </div>
              <div className={`text-[9px] mt-1 leading-none ${
                isToday ? 'text-amber-600 font-bold' : 'text-slate-400'
              }`}>
                {dayName(d.date, isToday)}
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend nhỏ */}
      <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-indigo-400 inline-block" />
          Thẻ mới
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-emerald-400 inline-block" />
          Ôn lại
        </span>
      </div>
    </div>
  );
}

// ============================================================
// UpcomingChart — 10 ngày tới
// ============================================================
function UpcomingChart({ daily, compact = false }) {
  if (!Array.isArray(daily) || daily.length === 0) return null;
  const max = Math.max(1, ...daily.map(d => d.count));
  const total = daily.reduce((s, d) => s + d.count, 0);
  const avg = Math.round(total / daily.length);

  return (
    <div className={compact ? '' : 'mt-4 pt-3 border-t border-slate-100'}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
          📅 Số thẻ phải học trong 10 ngày tới
        </div>
        <div className="text-[11px] text-slate-500">
          Tổng <b className="text-slate-700">{total}</b>
          {total > 0 && <> · TB <b className="text-slate-700">{avg}</b>/ngày</>}
        </div>
      </div>
      <div className="flex gap-1">
        {daily.map((d, i) => {
          const heightPct = max > 0 ? (d.count / max) * 100 : 0;
          const dayLabel = d.date.slice(8, 10) + '/' + d.date.slice(5, 7);
          const barColor =
            d.count === 0
              ? 'bg-slate-100'
              : d.count >= max * 0.7
              ? 'bg-rose-400 group-hover:bg-rose-500'
              : d.count >= max * 0.4
              ? 'bg-amber-400 group-hover:bg-amber-500'
              : 'bg-sky-400 group-hover:bg-sky-500';
          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center group"
              title={`${d.date}: ${d.count} thẻ đến hạn`}
            >
              <div className="text-[10px] text-slate-600 font-semibold h-4 leading-none">
                {d.count > 0 ? d.count : ''}
              </div>
              <div className="h-36 w-full flex items-end">
                <div
                  className={`w-full rounded-t transition-all ${barColor}`}
                  style={{ height: `${Math.max(3, heightPct)}%` }}
                />
              </div>
              <div className="text-[9px] text-slate-400 mt-1 leading-none">{dayLabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// HistoryHeatmap — calendar kiểu Anki (M41 — 2026-04-24 v2)
// Mỗi ô = 1 ngày. Grid 7 hàng (T2→CN) × 13 cột (~91 ngày).
// 3 level:
//   none    : ô trống — không học
//   partial : nửa tam giác nhạt — có học nhưng chưa đạt cả 2 chỉ tiêu
//   full    : tam giác đậm đầy — đạt CẢ 2 chỉ tiêu (mới ≥ min AND ôn ≥ min)
// ============================================================
const WEEKDAY_LABELS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const LEVEL_LABELS = {
  none:    'Không học',
  partial: 'Có học, chưa đạt mục tiêu',
  full:    'Đạt mục tiêu (đủ mới + đủ ôn)',
};

function HistoryHeatmap({ days, stats }) {
  if (!Array.isArray(days) || days.length === 0) return null;

  // Tổ chức theo tuần (cột): bắt đầu tuần là Thứ 2.
  // getDay(): 0=CN, 1=T2, ..., 6=T7 → map sang offset T2=0..CN=6
  function weekdayIndex(isoDate) {
    const d = new Date(isoDate + 'T00:00:00');
    const dow = d.getDay();
    return dow === 0 ? 6 : dow - 1;  // CN(0) → 6; T2(1) → 0
  }

  // M85c (2026-04-26): Append 14 ngày future (2 tuần tới) làm ô trống
  // → NV thấy "khoảng trống cần lấp" + grid không cụt ở bên phải.
  const FUTURE_DAYS = 14;
  const todayIsoOriginal = days[days.length - 1].date;
  const todayDate = new Date(todayIsoOriginal + 'T00:00:00');
  const futureDays = [];
  for (let i = 1; i <= FUTURE_DAYS; i++) {
    const d = new Date(todayDate.getTime() + i * 86400_000);
    futureDays.push({
      date: d.toISOString().slice(0, 10),
      level: 'none',
      new_cards: 0, reviews: 0, total: 0,
      future: true,  // → DayCell render style khác
    });
  }
  const allDays = [...days, ...futureDays];

  // Pad ngày đầu để ô đầu tuần đầu tiên align với Thứ 2
  const firstDate = allDays[0].date;
  const padStart = weekdayIndex(firstDate);
  const paddedDays = [
    ...Array(padStart).fill(null),
    ...allDays,
  ];
  // Chia thành tuần (mỗi tuần 7 ô)
  const weeks = [];
  for (let i = 0; i < paddedDays.length; i += 7) {
    weeks.push(paddedDays.slice(i, i + 7));
  }

  // Nhãn tháng cho mỗi tuần: ghi tên tháng vào tuần đầu có ngày 1-7 của tháng đó
  // (giúp user biết đoạn nào thuộc tháng nào)
  const monthLabels = weeks.map((wk, wi) => {
    for (const day of wk) {
      if (!day) continue;
      const dayNum = parseInt(day.date.slice(8, 10), 10);
      if (dayNum <= 7) {
        const month = parseInt(day.date.slice(5, 7), 10);
        return `T${month}`;
      }
    }
    return '';
  });

  const todayIso = todayIsoOriginal;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      {/* Header với 2 stat lớn */}
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
            📊 Lịch sử học
          </div>
        </div>
        <div className="flex gap-3 text-right">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">Đã học</div>
            <div className="text-lg font-bold text-slate-800 tabular-nums leading-tight">
              {stats.days_studied} <span className="text-xs font-normal text-slate-500">ngày</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">Đạt chỉ tiêu</div>
            <div className="text-lg font-bold text-emerald-700 tabular-nums leading-tight">
              {stats.days_met_target} <span className="text-xs font-normal text-slate-500">ngày</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">Chuỗi dài nhất</div>
            <div className="text-lg font-bold text-amber-700 tabular-nums leading-tight">
              🔥 {stats.best_streak} <span className="text-xs font-normal text-slate-500">ngày</span>
            </div>
          </div>
        </div>
      </div>

      {/* Month labels row (phía trên grid) */}
      <div className="flex gap-[3px] ml-6 mb-1">
        {monthLabels.map((lbl, i) => (
          <div key={i} className="w-3.5 text-[9px] text-slate-400 text-center leading-none">
            {lbl}
          </div>
        ))}
      </div>

      {/* Grid calendar: 7 rows × N columns */}
      <div className="flex gap-[3px]">
        {/* Weekday labels */}
        <div className="flex flex-col gap-[3px] mr-1 shrink-0">
          {WEEKDAY_LABELS.map((lbl, i) => (
            <div key={i} className="h-3.5 text-[9px] text-slate-400 leading-none flex items-center">
              {i % 2 === 0 ? lbl : ''}
            </div>
          ))}
        </div>

        {/* Weeks */}
        <div className="flex gap-[3px] flex-1 overflow-x-auto">
          {weeks.map((wk, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {Array.from({ length: 7 }).map((_, di) => {
                const day = wk[di];
                if (!day) {
                  return <div key={di} className="w-4 h-4" />;
                }
                const isToday = day.date === todayIso;
                return (
                  <DayCell
                    key={di}
                    day={day}
                    isToday={isToday}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-3 mt-3 text-[10px] text-slate-500 flex-wrap">
        <LegendItem level="none" label="Không học" />
        <LegendItem level="partial" label="Có học, chưa đạt" />
        <LegendItem level="full" label="Đạt mục tiêu" />
      </div>
      {(stats.daily_min_target > 0 || stats.daily_review_min > 0) && (
        <div className="text-[10px] text-slate-400 text-center mt-1">
          Mục tiêu/ngày: <b>{stats.daily_min_target}</b> thẻ mới + <b>{stats.daily_review_min}</b> thẻ ôn
        </div>
      )}
    </div>
  );
}

// Ô 1 ngày: SVG tam giác bên trong hình vuông
function DayCell({ day, isToday, cellSize = 16 }) {
  // M85c: ô future (2 tuần tới) — mờ hơn, dashed border, không hover
  if (day.future) {
    return (
      <div
        className="rounded-sm bg-slate-50 border border-dashed border-slate-200 opacity-60"
        style={{ width: cellSize, height: cellSize }}
        title={`${day.date}\n(Chưa đến)`}
      />
    );
  }
  return (
    <div
      className={`rounded-sm bg-white border border-slate-200 relative ${
        isToday ? 'ring-2 ring-amber-400 ring-offset-1' : ''
      } transition-transform hover:scale-125 cursor-pointer`}
      style={{ width: cellSize, height: cellSize }}
      title={`${day.date}\n📘 Mới: ${day.new_cards} · 🔁 Ôn: ${day.reviews} · Tổng: ${day.total}\n${LEVEL_LABELS[day.level] || ''}`}
    >
      <DayTriangle level={day.level} size={cellSize} />
    </div>
  );
}

// SVG tam giác nội tiếp ô vuông
//  - full: tam giác đậm đầy (emerald-600)
//  - partial: outline tam giác + fill NỬA TRÁI màu nhạt (emerald-200)
//  - none: không render gì
function DayTriangle({ level, size = 16 }) {
  if (level === 'none') return null;

  // Tam giác up-pointing nội tiếp, có padding 2px
  const pad = 2;
  const top = { x: size / 2, y: pad };
  const left = { x: pad, y: size - pad };
  const right = { x: size - pad, y: size - pad };
  const fullPath = `M ${top.x} ${top.y} L ${right.x} ${right.y} L ${left.x} ${left.y} Z`;
  // Nửa trái: từ đỉnh → đáy-trái → điểm giữa đáy → về đỉnh
  const halfLeftPath = `M ${top.x} ${top.y} L ${top.x} ${right.y} L ${left.x} ${left.y} Z`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="absolute inset-0 pointer-events-none"
    >
      {level === 'full' && (
        <path d={fullPath} fill="#059669" />
      )}
      {level === 'partial' && (
        <>
          <path d={halfLeftPath} fill="#a7f3d0" />
          <path d={fullPath} fill="none" stroke="#10b981" strokeWidth="1" />
        </>
      )}
    </svg>
  );
}

function LegendItem({ level, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-4 h-4 rounded-sm bg-white border border-slate-200 relative inline-block">
        <DayTriangle level={level} size={16} />
      </span>
      {label}
    </span>
  );
}

// ============================================================
// Shell — card container (KHÔNG còn badge đỏ vì ô hero đã dominant)
// ============================================================
function Shell({ children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
      {children}
    </div>
  );
}
